/**
 * Aiuti delle viste della partita: data e ora del form, competizione scelta
 * o scritta al volo, totali di squadra del tabellino, descrizione di un
 * evento, chi ha giocato nei voti.
 */

import type { RuoloGiocatore, StatisticheGiocatore, Tabellino, TipoEvento } from '@calcio/shared';
import { dataLocale, istanteLocale, oraLocale, stessoNome } from '@calcio/shared';
import { formatoIstante } from '../comuni/date.js';
import type { StatoPartita } from './raggruppa.js';

const formatoGiornoPartita = formatoIstante({
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
});
const formatoOraPartita = formatoIstante({ hour: '2-digit', minute: '2-digit' });

/**
 * La riga sopra il titolo della partita, come nell'allenamento: "sab 20/09/26
 * 15:00 · Casa · Stadio comunale", il luogo solo se c'è.
 */
export function rigaSopraPartita(p: {
  dataOra: string;
  inCasa: boolean;
  luogo: string | null;
}): string {
  const quando = new Date(p.dataOra);
  return [
    `${formatoGiornoPartita.format(quando)} ${formatoOraPartita.format(quando)}`,
    p.inCasa ? 'Casa' : 'Trasferta',
    p.luogo,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Istante ISO -> giorno e ora dei campi, come "2026-09-23T18:00", nell'ora
 * italiana: chi crea la partita ragiona nell'ora del campo, non in UTC ne nel
 * fuso del telefono. I form lo dividono tra il giorno e l'ora.
 */
export const perInputDataOra = (iso: string): string => `${dataLocale(iso)}T${oraLocale(iso)}`;

/** Giorno e ora dei campi, letti nell'ora italiana -> istante ISO; vuota se manca o non e valido. */
export function daInputDataOra(valore: string): string {
  const parti = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})$/.exec(valore);
  if (!parti) return '';
  const istante = istanteLocale(parti[1]!, parti[2]!);
  return Number.isNaN(istante.getTime()) ? '' : istante.toISOString();
}

/**
 * Competizione da inviare: quella esistente con lo stesso nome, a meno di
 * maiuscole e spazi, oppure il nome da creare. "coppa italia" scritto a
 * mano diventa la "Coppa Italia" che c'e gia.
 */
export function sceltaCompetizione(
  testo: string,
  esistenti: readonly { id: string; nome: string }[],
): { id: string } | { nome: string } {
  const trovata = esistenti.find((c) => stessoNome(c.nome, testo));
  return trovata ? { id: trovata.id } : { nome: testo };
}

/**
 * La disattivata con il nome scritto, a meno di maiuscole e spazi: il server
 * la riusa e la riattiva, e il form lo dice prima di salvare.
 */
export function disattivataConNome(
  testo: string,
  disattivate: readonly { id: string; nome: string }[],
): { id: string; nome: string } | undefined {
  return disattivate.find((c) => stessoNome(c.nome, testo));
}

export type TotaliSquadra = Pick<
  StatisticheGiocatore,
  | 'gol'
  | 'autogol'
  | 'assist'
  | 'tiriSpecchio'
  | 'tiriFuori'
  | 'passaggiCompletati'
  | 'passaggiSbagliati'
  | 'palleConquistate'
  | 'pallePerse'
  | 'gialli'
  | 'rossi'
>;

/** Statistiche di squadra: la somma di quelle individuali, niente di memorizzato. */
export function totaliSquadra(statistiche: readonly StatisticheGiocatore[]): TotaliSquadra {
  const totali: TotaliSquadra = {
    gol: 0,
    autogol: 0,
    assist: 0,
    tiriSpecchio: 0,
    tiriFuori: 0,
    passaggiCompletati: 0,
    passaggiSbagliati: 0,
    palleConquistate: 0,
    pallePerse: 0,
    gialli: 0,
    rossi: 0,
  };
  for (const s of statistiche) {
    for (const k of Object.keys(totali) as (keyof TotaliSquadra)[]) totali[k] += s[k];
  }
  return totali;
}

export const ETICHETTA_EVENTO: Record<TipoEvento, string> = {
  passaggio_completato: 'Passaggio riuscito',
  passaggio_sbagliato: 'Passaggio sbagliato',
  palla_conquistata: 'Palla recuperata',
  palla_persa: 'Palla persa',
  tiro_specchio: 'Tiro in porta',
  tiro_fuori: 'Tiro fuori',
  gol: 'Gol',
  autogol: 'Autogol',
  assist: 'Assist',
  cartellino_giallo: 'Giallo',
  cartellino_rosso: 'Rosso',
  ingresso: 'Entra',
  uscita: 'Esce',
  gol_subito: 'Gol subito',
  autogol_avversario: 'Autogol avversario',
  inizio_periodo: 'Inizio periodo',
  fine_periodo: 'Fine periodo',
};

export interface NomeGiocatore {
  id: string;
  nome: string;
  cognome: string;
  numero: number | null;
}

/**
 * Il punteggio come si legge nel calcio, prima la squadra di casa: in
 * trasferta i gol dell'avversario vengono prima dei nostri.
 */
export const punteggio = (
  r: { golFatti: number; golSubiti: number },
  inCasa: boolean,
  separatore = ' – ',
): string =>
  inCasa ? `${r.golFatti}${separatore}${r.golSubiti}` : `${r.golSubiti}${separatore}${r.golFatti}`;

/** "7 Rossi M." per le righe dove lo spazio conta. */
export const nomeBreve = (g: NomeGiocatore | undefined): string =>
  g ? `${g.numero != null ? `${g.numero} ` : ''}${g.cognome} ${g.nome.charAt(0)}.` : '—';

export const ETICHETTA_STATO_PARTITA: Record<StatoPartita, string> = {
  programmata: 'Programmata',
  formazione: 'Formazione pronta',
  in_corso: 'In corso',
  conclusa: 'Conclusa',
  annullata: 'Annullata',
};

/** Un giocatore nei voti della partita (V27): solo chi ha partecipazioni. */
export interface GiocatoreVotato {
  id: string;
  nome: string;
  cognome: string;
  numero: number | null;
  ruolo: RuoloGiocatore | null;
  titolare: boolean;
  minuti: number;
  voto: number | null;
}

export interface VotiPartita {
  partita: {
    id: string;
    squadraStagioneId: string;
    stato: StatoPartita;
    avversario: string;
    inCasa: boolean;
    dataOra: string;
    competizione: string;
  };
  giocatori: GiocatoreVotato[];
}

/** Titolari prima, poi per minuti giocati: l'ordine della formazione nel tabellino. */
export function ordinaChiHaGiocato<G extends { titolare: boolean; minuti: number }>(
  giocatori: readonly G[],
): G[] {
  return [...giocatori].sort(
    (a, b) => Number(b.titolare) - Number(a.titolare) || b.minuti - a.minuti,
  );
}

export const descriviPartecipazione = (g: { titolare: boolean; minuti: number }): string =>
  `${g.titolare ? 'Titolare' : 'Subentrato'} · ${g.minuti}'`;

export type TipoSegnale = 'entrata' | 'uscita' | 'giallo' | 'rosso';
export interface Segnale {
  tipo: TipoSegnale;
  /** Il minuto come si legge, recupero compreso: «45+2'». */
  minuto: string;
}

/**
 * Entrate, uscite e cartellini di ciascun giocatore, in ordine di minuto,
 * dalla cronaca: chi rientra, dove il formato lo consente, ha più entrate.
 */
export function segnaliPerGiocatore(voci: Tabellino['voci']): Map<string, Segnale[]> {
  const per = new Map<string, Segnale[]>();
  const aggiungi = (id: string | null | undefined, tipo: TipoSegnale, minuto: string) => {
    if (id) per.set(id, [...(per.get(id) ?? []), { tipo, minuto }]);
  };
  for (const v of voci) {
    if (v.tipo === 'sostituzione') {
      aggiungi(v.giocatoreUscenteId, 'uscita', v.etichetta);
      aggiungi(v.giocatoreId, 'entrata', v.etichetta);
    } else if (v.tipo === 'cartellino_giallo') {
      aggiungi(v.giocatoreId, 'giallo', v.etichetta);
    } else if (v.tipo === 'cartellino_rosso') {
      aggiungi(v.giocatoreId, 'rosso', v.etichetta);
    }
  }
  return per;
}
